import { useState, useCallback, useRef } from 'react';
import {
  AIAssistConversationMessage,
  AIAssistMode,
  AIAssistanceState,
  DiffChunk,
  HashLineEdit,
} from '../types';
import { requestAssistance } from '../services/ai';
import { resolveHashLineEdit } from '../services/hashline';
import { ProjectFile } from '../types';

const MAX_COMPILE_REPAIR_ATTEMPTS = 2;

interface UseAIAssistanceProps {
  projectId: string;
  activeFile: ProjectFile | null;
  allFiles: ProjectFile[];
  onApplyChanges: (newContent: string) => void;
  validateChanges: (newContent: string) => Promise<ValidationResult>;
}

interface UseAIAssistanceReturn extends AIAssistanceState {
  requestAI: (
    prompt: string,
    selection?: { startLine: number; endLine: number; text: string },
    conversationHistory?: AIAssistConversationMessage[],
    mode?: AIAssistMode
  ) => Promise<{ summary: string | null; error: string | null }>;
  acceptChunk: (chunkId: string) => Promise<void>;
  rejectChunk: (chunkId: string) => void;
  cancel: () => void;
  clearError: () => void;
  hasPendingReview: boolean;
  applyingChunkId: string | null;
}

interface LastAIRequest {
  prompt: string;
  selection?: { startLine: number; endLine: number; text: string };
  conversationHistory?: AIAssistConversationMessage[];
  mode?: AIAssistMode;
}

interface ValidationResult {
  ok: boolean;
  error?: string;
}

interface RepairAcceptedChunkResult {
  success: boolean;
  chunk?: DiffChunk;
  newContent?: string;
  error: string;
}

function buildCompileRepairPrompt(
  originalPrompt: string,
  chunk: DiffChunk,
  compileError: string,
  attempt: number
): string {
  return [
    'The accepted AI edit below failed to compile.',
    attempt > 1
      ? `This is repair attempt ${attempt}. The previous rewrite still failed to compile.`
      : 'Rewrite only this edit so the document compiles while preserving the original intent.',
    `Original user request:\n${originalPrompt}`,
    `Failed edit operation: ${chunk.op}`,
    `Target lines: ${chunk.startLine}-${chunk.endLine}`,
    `Original source being replaced:\n${chunk.original || '(insertion)'}`,
    `Failed suggested replacement:\n${chunk.suggested}`,
    `Compiler feedback:\n${compileError}`,
    [
      'Requirements:',
      '- Return exactly one edit.',
      '- Target only the same source region as the failed edit.',
      '- Do not introduce unrelated changes anywhere else in the file.',
      '- Preserve document structure and formatting outside this edit.',
    ].join('\n'),
  ].join('\n\n');
}

function replaceChunk(chunks: DiffChunk[], chunkId: string, replacement: DiffChunk): DiffChunk[] {
  return chunks.map((chunk) => (chunk.id === chunkId ? replacement : chunk));
}

function buildChunkFromEdit(content: string, edit: HashLineEdit): DiffChunk {
  const resolved = resolveHashLineEdit(content, edit);
  const lines = content.split('\n');
  const anchorBefore = resolved.startLine > 1 ? lines[resolved.startLine - 2] : '';
  const anchorAfter = resolved.endLine < lines.length ? lines[resolved.endLine] : '';

  return {
    id: edit.id,
    op: edit.op,
    pos: edit.pos,
    end: edit.end,
    startLine: resolved.startLine,
    endLine: Math.max(resolved.startLine, resolved.endLine),
    anchorBefore,
    anchorAfter,
    original: resolved.original,
    suggested: resolved.suggested,
    explanation: edit.explanation,
    status: 'pending',
  };
}

export function useAIAssistance({
  projectId,
  activeFile,
  allFiles,
  onApplyChanges,
  validateChanges,
}: UseAIAssistanceProps): UseAIAssistanceReturn {
  const [state, setState] = useState<AIAssistanceState>({
    loading: false,
    error: null,
    chunks: [],
    summary: null,
  });
  const [applyingChunkId, setApplyingChunkId] = useState<string | null>(null);
  const lastRequestRef = useRef<LastAIRequest | null>(null);

  const applyReviewedChunk = useCallback((content: string, chunk: DiffChunk): string => {
    const lines = content.split('\n');
    const startIndex = Math.max(0, chunk.startLine - 1);
    const endIndex = Math.max(startIndex, chunk.endLine);
    const currentOriginal = lines.slice(startIndex, endIndex).join('\n');

    if (currentOriginal !== chunk.original) {
      throw new Error('The document changed during review. Re-run AI assist.');
    }

    return [
      ...lines.slice(0, startIndex),
      ...chunk.suggested.split('\n'),
      ...lines.slice(endIndex),
    ].join('\n');
  }, []);

  const rebaseRemainingChunks = useCallback((chunks: DiffChunk[], acceptedChunk: DiffChunk): DiffChunk[] => {
    const originalLineCount = acceptedChunk.original === '' ? 0 : acceptedChunk.original.split('\n').length;
    const suggestedLineCount = acceptedChunk.suggested === '' ? 0 : acceptedChunk.suggested.split('\n').length;
    const delta = suggestedLineCount - originalLineCount;

    return chunks.map((chunk) => {
      if (chunk.endLine < acceptedChunk.startLine) {
        return chunk;
      }

      if (chunk.startLine > acceptedChunk.endLine) {
        return {
          ...chunk,
          startLine: chunk.startLine + delta,
          endLine: chunk.endLine + delta,
        };
      }

      throw new Error('AI suggested overlapping edits. Re-run AI assist.');
    });
  }, []);

  const ensureChunksStillMatch = useCallback((content: string, chunks: DiffChunk[]): DiffChunk[] => {
    const lines = content.split('\n');

    for (const chunk of chunks) {
      const startIndex = Math.max(0, chunk.startLine - 1);
      const endIndex = Math.max(startIndex, chunk.endLine);
      const currentOriginal = lines.slice(startIndex, endIndex).join('\n');

      if (currentOriginal !== chunk.original) {
        throw new Error(
          'Remaining AI suggestions became ambiguous after the last accepted edit. Re-run AI assist.'
        );
      }
    }

    return chunks;
  }, []);

  const fetchSuggestions = useCallback(async (
    prompt: string,
    file: ProjectFile,
    files: ProjectFile[],
    selection?: { startLine: number; endLine: number; text: string },
    conversationHistory?: AIAssistConversationMessage[],
    mode: AIAssistMode = 'edits'
  ) => {
    const response = await requestAssistance(
      projectId,
      prompt,
      file,
      files,
      selection,
      conversationHistory,
      mode
    );

    return {
      summary: response.summary,
      chunks: 'edits' in response
        ? response.edits.map((edit) => buildChunkFromEdit(file.content, edit))
        : response.chunks,
    };
  }, [projectId]);

  const repairAcceptedChunk = useCallback(async (
    chunk: DiffChunk,
    file: ProjectFile,
    files: ProjectFile[],
    compileError: string
  ): Promise<RepairAcceptedChunkResult> => {
    if (!lastRequestRef.current) {
      return {
        success: false,
        error: compileError,
      };
    }

    let currentError = compileError;
    let latestChunk: DiffChunk | undefined;

    for (let attempt = 1; attempt <= MAX_COMPILE_REPAIR_ATTEMPTS; attempt += 1) {
      const repaired = await fetchSuggestions(
        buildCompileRepairPrompt(lastRequestRef.current.prompt, chunk, currentError, attempt),
        file,
        files,
        lastRequestRef.current.selection,
        lastRequestRef.current.conversationHistory,
        lastRequestRef.current.mode
      );

      if (repaired.chunks.length !== 1) {
        return {
          success: false,
          error: 'The AI could not rewrite this edit as a single compile-safe change. Review the suggestion again.',
        };
      }

      latestChunk = repaired.chunks[0];

      let newContent: string;
      try {
        newContent = applyReviewedChunk(file.content, latestChunk);
      } catch {
        return {
          success: false,
          error: 'The AI rewrite no longer matches the original source region. Review the suggestion again.',
        };
      }

      if (newContent === file.content) {
        return {
          success: false,
          error: 'The AI rewrite does not change the document. Review the suggestion again.',
        };
      }

      const validation = await validateChanges(newContent);
      if (validation.ok) {
        return {
          success: true,
          chunk: latestChunk,
          newContent,
          error: '',
        };
      }

      currentError = validation.error || 'The revised AI edit still does not compile.';
    }

    return {
      success: false,
      chunk: latestChunk,
      error: currentError,
    };
  }, [applyReviewedChunk, fetchSuggestions, validateChanges]);

  const requestAI = useCallback(async (
    prompt: string,
    selection?: { startLine: number; endLine: number; text: string },
    conversationHistory?: AIAssistConversationMessage[],
    mode: AIAssistMode = 'edits'
  ) => {
    if (!activeFile) {
      setState((prev) => ({ ...prev, error: 'No active file' }));
      return { summary: null, error: 'No active file' };
    }

    setState((prev) => ({ ...prev, loading: true, error: null }));
    lastRequestRef.current = { prompt, selection, conversationHistory, mode };

    try {
      const response = await fetchSuggestions(
        prompt,
        activeFile,
        allFiles,
        selection,
        conversationHistory,
        mode
      );
      setState({
        loading: false,
        error: null,
        chunks: response.chunks,
        summary: response.summary,
      });
      return { summary: response.summary, error: null };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to get AI assistance';
      setState((prev) => ({
        ...prev,
        loading: false,
        error: message,
      }));
      return { summary: null, error: message };
    }
  }, [activeFile, allFiles, fetchSuggestions]);

  const acceptChunk = useCallback(async (chunkId: string) => {
    if (!activeFile) return;

    const chunk = state.chunks.find((candidate) => candidate.id === chunkId);
    if (!chunk) return;

    setApplyingChunkId(chunkId);

    try {
      let acceptedChunk = chunk;
      let newContent = applyReviewedChunk(activeFile.content, chunk);
      if (newContent === activeFile.content) {
        throw new Error('This AI edit does not change the document.');
      }

      const validation = await validateChanges(newContent);
      if (!validation.ok) {
        setState((prev) => ({
          ...prev,
          error: null,
          summary: 'Accepted edit failed to compile. Asking AI to rewrite it...',
        }));

        const repaired = await repairAcceptedChunk(
          chunk,
          activeFile,
          allFiles,
          validation.error || 'The AI edit does not compile.'
        );

        if (!repaired.success || !repaired.chunk || !repaired.newContent) {
          setState((prev) => ({
            ...prev,
            chunks: repaired.chunk ? replaceChunk(prev.chunks, chunkId, repaired.chunk) : prev.chunks,
            error: repaired.error || 'The AI edit does not compile. It was not applied.',
            summary: repaired.chunk
              ? 'Original accepted edit failed to compile. Review the revised suggestion.'
              : prev.summary,
          }));
          return;
        }

        acceptedChunk = repaired.chunk;
        newContent = repaired.newContent;
      }

      onApplyChanges(newContent);

      const remainingChunks = state.chunks.filter((candidate) => candidate.id !== chunkId);
      let rebasedChunks: DiffChunk[];
      try {
        rebasedChunks = ensureChunksStillMatch(
          newContent,
          rebaseRemainingChunks(remainingChunks, acceptedChunk)
        );
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to refresh AI suggestions';
        const shouldRefresh = message.includes('Remaining AI suggestions became ambiguous')
          || message.includes('overlapping edits');

        if (!shouldRefresh || !lastRequestRef.current) {
          throw err;
        }

        const updatedActiveFile = { ...activeFile, content: newContent };
        const updatedFiles = allFiles.map((file) => (
          file.id === activeFile.id ? updatedActiveFile : file
        ));

        setState((prev) => ({
          ...prev,
          loading: true,
          error: null,
          chunks: [],
          summary: 'Refreshing remaining suggestions for the updated document...',
        }));

        const refreshed = await fetchSuggestions(
          lastRequestRef.current.prompt,
          updatedActiveFile,
          updatedFiles,
          lastRequestRef.current.selection,
          lastRequestRef.current.conversationHistory,
          lastRequestRef.current.mode
        );

        setState({
          loading: false,
          error: null,
          chunks: refreshed.chunks,
          summary: refreshed.summary,
        });
        return;
      }

      if (rebasedChunks.length === 0) {
        setState({ loading: false, error: null, chunks: [], summary: null });
      } else {
        setState((prev) => ({ ...prev, chunks: rebasedChunks, error: null }));
      }
    } catch (err) {
      setState((prev) => ({
        ...prev,
        error: err instanceof Error ? err.message : 'Failed to apply AI edit',
      }));
    } finally {
      setApplyingChunkId(null);
    }
  }, [
    activeFile,
    allFiles,
    state.chunks,
    applyReviewedChunk,
    ensureChunksStillMatch,
    fetchSuggestions,
    onApplyChanges,
    repairAcceptedChunk,
    rebaseRemainingChunks,
    validateChanges,
  ]);

  const rejectChunk = useCallback((chunkId: string) => {
    const remainingChunks = state.chunks.filter((chunk) => chunk.id !== chunkId);

    if (remainingChunks.length === 0) {
      setState({ loading: false, error: null, chunks: [], summary: null });
    } else {
      setState((prev) => ({ ...prev, chunks: remainingChunks }));
    }
  }, [state.chunks]);

  const cancel = useCallback(() => {
    setApplyingChunkId(null);
    setState({ loading: false, error: null, chunks: [], summary: null });
  }, []);

  const clearError = useCallback(() => {
    setState((prev) => ({ ...prev, error: null }));
  }, []);

  const hasPendingReview = state.chunks.length > 0 && !state.loading;

  return {
    ...state,
    requestAI,
    acceptChunk,
    rejectChunk,
    cancel,
    clearError,
    hasPendingReview,
    applyingChunkId,
  };
}
